

/**
=========================================================
* Material Dashboard 2 React - v1.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { forwardRef } from "react";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// Custom styles for MDButton
import MDButtonRoot from "components/MDButton/MDButtonRoot";

// Material Dashboard 2 React contexts
import { useMaterialUIController } from "context";
import { useOnV2Strip, useInV2PageHeaderActions } from "components/MDBox";
import { v2 } from "styles/tokens/v2";

const MDButton = forwardRef(
  ({ color, variant, size, circular, iconOnly, children, ...rest }, ref) => {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    // v2 header strips are white. A white outlined or text button there turns
    // dark; a white filled one keeps its fill and gains a hairline border.
    const inStrip = useOnV2Strip();
    const onStrip = inStrip && color === "white";
    const filled = variant === "contained" || variant === "gradient";
    // A related-list card's dark "Add ..." button becomes the mockup's small
    // outlined card action ("Log activity"). Icon buttons keep their look.
    const cardAction = inStrip && filled && color === "dark" && !iconOnly;
    // The page header's dark filled button is the page's main action: brand blue.
    const headerPrimary = useInV2PageHeaderActions() && filled && color === "dark" && !iconOnly;
    const stripColor = headerPrimary ? "info" : (onStrip && !filled) || cardAction ? "dark" : color;
    const stripVariant = cardAction ? "outlined" : variant;
    const stripSize = cardAction ? "small" : size;
    let stripSx = onStrip && filled ? { boxShadow: `inset 0 0 0 1px ${v2.border}` } : null;
    if (cardAction) {
      stripSx = {
        backgroundColor: `${v2.surface} !important`,
        borderColor: `${v2.border2} !important`,
        color: `${v2.text} !important`,
        "&:hover": { backgroundColor: `${v2.hover} !important` },
      };
    }
    const sx = stripSx ? [...(Array.isArray(rest.sx) ? rest.sx : rest.sx ? [rest.sx] : []), stripSx] : rest.sx;

    return (
      <MDButtonRoot
        {...rest}
        sx={sx}
        ref={ref}
        color="primary"
        variant={stripVariant === "gradient" ? "contained" : stripVariant}
        size={stripSize}
        ownerState={{ color: stripColor, variant: stripVariant, size: stripSize, circular, iconOnly, darkMode }}
      >
        {children}
      </MDButtonRoot>
    );
  }
);

// Setting default values for the props of MDButton
MDButton.defaultProps = {
  size: "medium",
  variant: "contained",
  color: "white",
  circular: false,
  iconOnly: false,
};

// Typechecking props for the MDButton
MDButton.propTypes = {
  size: PropTypes.oneOf(["small", "medium", "large"]),
  variant: PropTypes.oneOf(["text", "contained", "outlined", "gradient"]),
  color: PropTypes.oneOf([
    "white",
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "light",
    "dark",
  ]),
  circular: PropTypes.bool,
  iconOnly: PropTypes.bool,
  children: PropTypes.node.isRequired,
};

export default MDButton;
